import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { HINDI_FONT } from "../../Temples/shared";
import { C, FONT, money, Detail, DetailsGrid, pillSx, pillPhoneSx, UrgentTemples } from "../../ThankyouPage/TempleThankYou/shared";
import HoveringKalash from "./HoveringKalash";
import circleX from "../../../assets/Images/TempleFailure/circle-x.svg";
import chipHeart from "../../../assets/Images/TempleFailure/chip-heart.svg";
import rotateCw from "../../../assets/Images/TempleFailure/rotate-cw.svg";
import headphones from "../../../assets/Images/TempleFailure/headphones.svg";

// Payment-failed page for temple payments — Figma "Temple — Payment failed (Generic)" (3718:8135).
// Same data as the regular failure page; PaymentFailurePage decides which one to show and keeps
// the shared behaviour (failed-attempt record, back-button popup).
// Desktop/tablet: the hero (scene → buttons) fits the window; short windows get tighter spacing,
// the scene takes the height that's left and, if even that doesn't fit, the block is scaled down
// evenly (never below 75%). Phones: a normal scrolling page like the mobile thank-you page (Figma
// "Mobile revamp" 802:1481) — the scene at the thank-you skyline's size, edges faded, no scaling.

const SCENE_W = 1344;
const SCENE_H = 392;
// Phones: about the height of the thank-you page's skyline (199px), so both pages read alike
const MOBILE_SCENE_H = 190;
// Phones: the panorama fades out at both screen edges, so it sits as one centred drawing instead of
// being cut off (-webkit- prefix for Safari)
const EDGE_FADE = "linear-gradient(90deg, transparent 0%, #000 16%, #000 84%, transparent 100%)";

const TempleFailure = ({ title, amount, tipAmount = 0, currency = "INR", donorName, dateLabel, reference, paymentMethod, isMonthly, campaignId, onRetry, onSupport }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isDesktop = useMediaQuery("(min-width:900px)");
  const compact = useMediaQuery("(max-height:1200px)") && isDesktop;
  const tight = useMediaQuery("(max-height:820px)") && isDesktop;
  const heroRef = useRef(null);
  const sceneRef = useRef(null);
  const actionsRef = useRef(null);
  const [sceneHeight, setSceneHeight] = useState(null);
  const minScene = 140;
  const minZoom = 0.75;
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  useLayoutEffect(() => {
    const fit = () => {
      if (isMobile) {
        // phones: the middle of the village at about the thank-you skyline's size, page scrolls normally
        zoomRef.current = 1;
        setZoom(1);
        setSceneHeight(MOBILE_SCENE_H);
        return;
      }
      const hero = heroRef.current;
      const scene = sceneRef.current;
      const actions = actionsRef.current;
      if (!hero || !scene || !actions) return;
      const z = zoomRef.current || 1;
      const heroTop = hero.getBoundingClientRect().top + window.scrollY;
      const sceneRect = scene.getBoundingClientRect();
      const below = (actions.getBoundingClientRect().bottom - sceneRect.bottom) / z; // text, card, buttons (unscaled)
      const above = sceneRect.top + window.scrollY - heroTop; // section top padding
      // 24px breathing room under the buttons; phones also keep clear of the floating WhatsApp button
      const free = window.innerHeight - heroTop - above - (isMobile ? 88 : 24);
      // Desktop: the scene can't be wider than the page. Phones crop the panorama to its middle,
      // so up to the 760px-wide crop's height (222px) is allowed.
      const maxScene = isMobile ? Math.round((760 * SCENE_H) / SCENE_W) : Math.min(SCENE_H, Math.floor((hero.clientWidth - 32) * (SCENE_H / SCENE_W)));
      let nextHeight;
      let nextZoom = 1;
      if (below + maxScene <= free) nextHeight = maxScene;
      else if (below + minScene <= free) nextHeight = Math.round(free - below);
      else {
        nextHeight = minScene;
        nextZoom = Math.max(minZoom, Math.floor((free / (below + minScene)) * 100) / 100);
      }
      zoomRef.current = nextZoom;
      setZoom(nextZoom);
      setSceneHeight((prev) => (prev && Math.abs(prev - nextHeight) < 2 ? prev : nextHeight));
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (actionsRef.current?.parentElement) ro.observe(actionsRef.current.parentElement);
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [isMobile, minScene, minZoom]);

  const total = Number(amount) || 0;
  const tip = Math.max(0, Number(tipAmount) || 0);
  const seva = Math.max(0, total - tip);
  const tipPct = seva > 0 && tip > 0 ? Math.round((tip / seva) * 100) : 0;

  return (
    <Box sx={{ background: "#FFFFFF", overflowX: "clip" }}>
      {/* ── Payment result ── */}
      <Box ref={heroRef} component="section" sx={{ pt: isMobile ? "12px" : compact ? "12px" : "24px", pb: isMobile ? "32px" : tight ? "48px" : "64px", px: "16px", ...(isMobile && { background: "linear-gradient(180deg, #FCF6ED 0px, #FFFFFF 260px)" }) }}>
        <Box sx={{ zoom }}>
        {/* On phones the panorama keeps its height and shows the middle of the village */}
        <Box ref={sceneRef} sx={{ mx: isMobile ? "-16px" : 0, overflow: "hidden", display: "flex", justifyContent: "center", ...(isMobile && { maskImage: EDGE_FADE, WebkitMaskImage: EDGE_FADE }) }}>
          <Box
            sx={{
              flexShrink: 0,
              width: sceneHeight ? `${Math.round((sceneHeight * SCENE_W) / SCENE_H)}px` : isMobile ? `${Math.round((MOBILE_SCENE_H * SCENE_W) / SCENE_H)}px` : "min(1344px, 100%)",
              transition: "width 0.2s ease",
            }}
          >
            <HoveringKalash width="100%" />
          </Box>
        </Box>

        <Box sx={{ maxWidth: "900px", mx: "auto", px: isMobile ? "8px" : 0, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 500, lineHeight: "20px", color: "#8A7658", mt: isMobile ? "8px" : tight ? "2px" : "8px", fontSize: isMobile ? "14px" : "15px" }}>
            The mandir is still waiting for your seva.
          </Typography>
          <Typography sx={{ fontFamily: HINDI_FONT, fontSize: isMobile ? "15px" : "18px", lineHeight: isMobile ? "normal" : "26px", color: C.gold, mt: isMobile ? "14px" : tight ? "8px" : compact ? "14px" : "28px" }}>
            ॥ सेवा परमो धर्मः ॥
          </Typography>
          <Typography component="h1" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "28px" : tight ? "36px" : compact ? "42px" : "52px", lineHeight: isMobile ? "34px" : tight ? "44px" : compact ? "50px" : "62px", color: C.ink, mt: isMobile ? "6px" : tight ? "2px" : compact ? "6px" : "12px" }}>
            Your payment{" "}
            <Box component="span" sx={{ color: C.red }}>
              failed.
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: isMobile ? "14px" : tight ? "15px" : compact ? "16px" : "17px", lineHeight: isMobile ? "21px" : tight ? "22px" : compact ? "24px" : "27px", color: C.muted, mt: isMobile ? "6px" : tight ? "4px" : compact ? "8px" : "12px", maxWidth: compact ? "720px" : "640px" }}>
            We’re sorry, your seva payment couldn’t be completed. You can try again when you’re ready.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: isMobile ? "14px" : tight ? "8px" : compact ? "10px" : "16px", pl: isMobile ? "12px" : "14px", pr: isMobile ? "14px" : "16px", py: isMobile ? "6px" : tight ? "6px" : "8px", borderRadius: "100px", background: "rgba(236,27,38,0.07)" }}>
            <Box component="img" src={circleX} alt="" sx={{ width: isMobile ? "15px" : "16px", height: isMobile ? "15px" : "16px" }} />
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.red }}>Payment failed</Typography>
          </Box>

          {/* Attempted payment summary */}
          <Box
            sx={{
              mt: isMobile ? "20px" : tight ? "12px" : compact ? "18px" : "30px",
              width: isMobile ? "calc(100% + 16px)" : "100%",
              maxWidth: "600px",
              boxSizing: "border-box",
              textAlign: "left",
              background: "#FFFFFF",
              border: "1px solid rgba(201,149,43,0.3)",
              borderRadius: "20px",
              boxShadow: isMobile ? "0px 12px 30px 0px rgba(89,51,13,0.07)" : "0px 16px 40px 0px rgba(89,51,13,0.07)",
              px: isMobile ? "18px" : "32px",
              py: isMobile ? "20px" : tight ? "16px" : compact ? "20px" : "28px",
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "14px" : tight ? "10px" : compact ? "12px" : "18px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "10px" : "11px", lineHeight: isMobile ? "normal" : "14px", letterSpacing: isMobile ? "1.2px" : "1.32px", color: C.gold }}>PAYMENT ATTEMPT SUMMARY</Typography>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "15px" : "17px", lineHeight: isMobile ? "20px" : "24px", color: C.ink, mt: "2px", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {title || "Temple seva"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", pl: "10px", pr: "12px", py: "6px", borderRadius: "100px", background: "rgba(236,27,38,0.07)", flexShrink: 0 }}>
                <Box component="img" src={chipHeart} alt="" sx={{ width: "13px", height: "13px" }} />
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "12px", lineHeight: "16px", color: C.red, whiteSpace: "nowrap" }}>{isMonthly ? "Monthly" : "One-time"}</Typography>
              </Box>
            </Box>

            <Box sx={{ background: C.cream, borderRadius: "14px", px: isMobile ? "16px" : "20px", py: isMobile ? "16px" : tight ? "12px" : "18px", display: "flex", flexDirection: "column", gap: isMobile ? "5px" : tight ? "8px" : "12px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "normal" : "20px", color: C.muted }}>Attempted amount</Typography>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "20px" : "32px", lineHeight: isMobile ? "normal" : "38px", color: C.ink, whiteSpace: "nowrap" }}>{money(total, currency)}</Typography>
              </Box>
              {tip > 0 && (
                <>
                  <Box sx={{ height: "1px", background: "rgba(201,149,43,0.25)" }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.muted }}>Temple seva</Typography>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.ink }}>{money(seva, currency)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.muted }}>TrueHope support{tipPct ? ` (${tipPct}%)` : ""}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.ink }}>{money(tip, currency)}</Typography>
                  </Box>
                </>
              )}
            </Box>

            <DetailsGrid phone={isMobile} isDesktop={isDesktop}>
              <Detail phone={isMobile} label="DONOR" value={donorName} />
              <Detail phone={isMobile} label="ATTEMPT DATE" value={dateLabel} />
              <Detail phone={isMobile} label="ATTEMPT REFERENCE" value={reference} />
              <Detail phone={isMobile} label="PAYMENT" value={paymentMethod} />
            </DetailsGrid>
          </Box>

          {/* Actions */}
          <Box ref={actionsRef} sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "10px" : "12px", mt: isMobile ? "20px" : tight ? "12px" : compact ? "16px" : "20px", flexWrap: isMobile ? "nowrap" : "wrap", justifyContent: "center", width: isMobile ? "calc(100% + 16px)" : "auto" }}>
            <Box component="button" type="button" onClick={onRetry} sx={{ ...pillSx, ...(isMobile && pillPhoneSx), border: "none", background: C.red, color: "#FFFFFF", boxShadow: "0px 8px 20px rgba(236,27,38,0.25)" }}>
              <Box component="img" src={rotateCw} alt="" sx={{ width: "16px", height: "16px" }} />
              Retry payment
            </Box>
            <Box component="button" type="button" onClick={onSupport} sx={{ ...pillSx, ...(isMobile && pillPhoneSx), border: `1px solid ${C.sand}`, background: "#FFFFFF", color: C.ink }}>
              <Box component="img" src={headphones} alt="" sx={{ width: "16px", height: "16px" }} />
              Contact support
            </Box>
          </Box>
          <Typography sx={{ fontFamily: FONT, lineHeight: "20px", color: C.muted, mt: isMobile ? "14px" : tight ? "10px" : compact ? "14px" : "21px", fontSize: isMobile ? "12px" : "13px", maxWidth: compact ? "720px" : "640px" }}>
            If an amount was debited, please contact support with the attempt reference above so we can help check your payment status.
          </Typography>
        </Box>
        </Box>
      </Box>

      <UrgentTemples campaignId={campaignId} />
    </Box>
  );
};

export default TempleFailure;
